import { useState, useEffect, useCallback } from 'react';
import { trainingService, TrainingCourse, CourseProgress, Certificate } from '../services/trainingService';

export const useTraining = () => {
  const [courses, setCourses] = useState<TrainingCourse[]>([]);
  const [myProgress, setMyProgress] = useState<CourseProgress[]>([]);
  const [myCertificates, setMyCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCourses = useCallback(async (params?: any) => {
    setLoading(true);
    setError(null);
    try {
      const data = await trainingService.getCourses(params);
      setCourses(data.courses);
      return data;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch courses');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyProgress = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await trainingService.getMyProgress();
      setMyProgress(data);
      return data;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch progress');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyCertificates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await trainingService.getMyCertificates();
      setMyCertificates(data);
      return data;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch certificates');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const enrollInCourse = useCallback(async (courseId: string) => {
    setLoading(true);
    setError(null);
    try {
      const progress = await trainingService.enrollInCourse(courseId);
      // Refresh progress list
      await fetchMyProgress();
      return progress;
    } catch (err: any) {
      setError(err.message || 'Failed to enroll in course');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchMyProgress]);

  const updateProgress = useCallback(async (courseId: string, data: any) => {
    try {
      const progress = await trainingService.updateProgress(courseId, data);
      // Update local state
      setMyProgress(prev => 
        prev.map(p => p.courseId === courseId ? progress : p)
      );
      return progress;
    } catch (err: any) {
      setError(err.message || 'Failed to update progress');
      throw err;
    }
  }, []);

  return {
    courses,
    myProgress,
    myCertificates,
    loading,
    error,
    fetchCourses,
    fetchMyProgress,
    fetchMyCertificates,
    enrollInCourse,
    updateProgress,
  };
};

export const useVideoProgress = (courseId: string, videoDuration: number) => {
  const [currentPosition, setCurrentPosition] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const saveProgress = useCallback(async (position: number) => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      await trainingService.updateProgress(courseId, {
        currentPosition: Math.floor(position),
        videoProgress: Math.floor(position),
      });
    } catch (error) {
      console.error('Failed to save progress:', error);
    } finally {
      setIsSaving(false);
    }
  }, [courseId, isSaving]);

  const updatePosition = useCallback((position: number) => {
    setCurrentPosition(position);
    
    // Save progress every 10 seconds
    if (Math.floor(position) % 10 === 0) {
      saveProgress(position);
    }
  }, [saveProgress]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (currentPosition > 0) {
        saveProgress(currentPosition);
      }
    };
  }, [currentPosition, saveProgress]);

  return {
    currentPosition,
    updatePosition,
    saveProgress,
  };
};

